import type { TAutomationRunningState } from '@/modules/automation/components/AutomationDocumentTitleBar';
import type { ICodeManifest } from './code-manifest';

interface IDeriveDocumentRunningStateArgs {
  /** Parsed manifest, for the total step count. */
  codeManifest: ICodeManifest | null | undefined;
  /**
   * Step number the run has actually reported via its latest `node_started`
   * event. `null`/`undefined` means the run has not named a step yet — a
   * queued run, or one whose journal has not arrived.
   */
  reportedStep: number | null | undefined;
  isAwaitingGuidance: boolean;
  isTerminal: boolean;
}

/**
 * Live run state for the document title bar.
 *
 * Unlike the AOP's `running` highlight this is not suppressed while the run is
 * parked on guidance: a paused run still has a step it is parked *at*, and
 * naming it is the whole point of the paused variant.
 *
 * The step number is only ever the one the run reported. A queued run, or the
 * window before the journal resolves, yields an `executing` state with no step
 * rather than guessing "Step 1" for work that has not started. Terminal runs
 * yield nothing, which drops the bar back to its stage meta text.
 */
export function deriveDocumentRunningState({
  codeManifest,
  reportedStep,
  isAwaitingGuidance,
  isTerminal,
}: IDeriveDocumentRunningStateArgs): TAutomationRunningState | undefined {
  const totalSteps = codeManifest?.steps?.length ?? 0;
  if (totalSteps === 0) return undefined;

  const step =
    reportedStep != null && reportedStep > 0
      ? Math.min(reportedStep, totalSteps)
      : undefined;

  if (isAwaitingGuidance) return { state: 'awaiting-guidance', step };
  if (isTerminal) return undefined;
  return { state: 'executing', step, totalSteps };
}
